import { Component, Inject, HostListener } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Case, ActionType, NewsStory, CaseState, Action, nextTerm, ExceptionalTiming, Supervisor, Position, CaseReconciliation, ReconChange } from '../../../domain/types/ahr';
import { AHRService, Option, ActionProposal, TitleRankStepAndTermId, ActionProposalType, ProgressionTable, Advancement, User, SortedSet } from '../../../domain';
import { ConsoleService } from '../../../widgets/console.service';
import { AdvancementSelectorModalComponent, AdvancementSelectorResult } from '../advancement-selector-modal/advancement-selector-modal.component';
import { FormControl } from '@angular/forms';
import { HtmlContentModalComponent } from '../../../main-core/html-content-modal/html-content-modal.component';
import { map, filter } from 'rxjs/operators';
import { GoogleAnalyticsService } from '../../../main-core/google-analytics.service';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { WorkflowService } from '../../../data-access/workflow.service';
import { ActiveWorkflowCase, FROZEN_STAGE_WORKFLOW_TYPES, STAGE_DISPLAY_NAMES } from '../../../domain/types/workflow';

export class CaseModalNavigator {
	static noop(): CaseModalNavigator {
		return new CaseModalNavigator(
			() => false,
			() => false,
			_ => {},
			_ => {});
	}
	constructor(
		readonly hasPrev: () => boolean,
		readonly hasNext: () => boolean,
		readonly goPrev: (cmc: CaseModalComponent) => void,
		readonly goNext: (cmc: CaseModalComponent) => void
	){ }
	public tryGoNext(cmc: CaseModalComponent): void {
		if(this.hasNext()){
			this.goNext(cmc);
		}
	}
	public tryGoPrev(cmc: CaseModalComponent): void {
		if(this.hasPrev()){
			this.goPrev(cmc);
		}
	}
}

const formatShortDate = (d: Date | null): string => {
    if (!d) return '';
    return (String(d.getMonth() + 1).padStart(2, '0') + '/' + 
        String(d.getDate()).padStart(2, '0') + '/' + 
        d.getFullYear());
};

@Component({
	selector: 'case-modal',
	templateUrl: './case-modal.component.html',
	styleUrls: ['./case-modal.component.scss']
})
export class CaseModalComponent {
	ActionProposalType = ActionProposalType;
	ExceptionalTiming = ExceptionalTiming;
	usersControl: FormControl;
	supervisorReviewInvolvementControl: FormControl;
	private static readonly HISTORY_TAB = { label: "History", id: "history" };
	private static readonly COMPACT_BREAKPOINT = 1250; // px — below this, History becomes a tab

	nonacademic_tabs = [
		{ label: "Case Details"       , id: "case" }
	   ,{ label: "County Assignments" , id: "county_assignments" }
   	];
	academic_tabs = [
		 { label: "Case Details"       , id: "case" }
		,{ label: "Candidate Documents", id: "candidate_docs" }
		,{ label: "Supervisors"        , id: "supervisors" }
		,{ label: "County Assignments" , id: "county_assignments" }
		,{ label: "Academic Info"   , id: "academic_info"}
		,{ label: "Case Tasks"         , id: "case_tasks" }
	];
	tabs = this.academic_tabs;
	activeTab = this.tabs[0];
	showHistorySidebar = true;

	@HostListener('window:resize')
	onResize() {
		this.updateHistoryLayout();
	}

	private updateHistoryLayout() {
		const compact = window.innerWidth < CaseModalComponent.COMPACT_BREAKPOINT;
		if (compact === !this.showHistorySidebar) return; // no change
		this.showHistorySidebar = !compact;
		this.rebuildTabs();
	}

	private rebuildTabs() {
		const baseTabs = this.case && this.isNonAcademic(this.case)
			? this.nonacademic_tabs
			: this.academic_tabs;
		if (this.showHistorySidebar) {
			this.tabs = baseTabs;
			// If user was on the History tab, switch back to Case Details
			if (this.activeTab.id === 'history') {
				this.activeTab = this.tabs[0];
			}
		} else {
			this.tabs = [...baseTabs, CaseModalComponent.HISTORY_TAB];
		}
	}
	activateTab(tab){
		this.console.log('activating tab: ', tab);
		this.activeTab = tab;
	}

	private changeKey(c: ReconChange): string { return `${c.stage}|${c.kind}|${c.userId}|${c.taskId ?? ''}`; }
	isChangeSelected(c: ReconChange): boolean { return this.selectedChangeKeys.has(this.changeKey(c)); }
	toggleChange(c: ReconChange): void {
		const k = this.changeKey(c);
		if (this.selectedChangeKeys.has(k)) this.selectedChangeKeys.delete(k); else this.selectedChangeKeys.add(k);
	}
	get hasDrift(): boolean { return !!this.reconciliation && this.reconciliation.stages.some(s => s.changes.length > 0); }
	get hasSelectedChanges(): boolean { return this.selectedChangeKeys.size > 0; }

	private pruneSelection(): void {
		const validKeys = new Set<string>();
		if (this.reconciliation) {
			for (const stage of this.reconciliation.stages) {
				for (const ch of stage.changes) {
					validKeys.add(this.changeKey(ch));
				}
			}
		}
		for (const k of Array.from(this.selectedChangeKeys)) {
			if (!validKeys.has(k)) this.selectedChangeKeys.delete(k);
		}
	}

	onReviewStepsChanged(): void {
		this.fetchFullCase(this.case.id);
		this.refreshReconciliation();
	}

	refreshReconciliation(): void {
		const caseId = this.activeWorkflowCase?.caseId;
		if (!caseId) { this.reconciliation = null; this.selectedChangeKeys.clear(); return; }
		this.reconciliationLoading = true;
		this.svc.getCaseReconciliation(caseId).then(r => {
			this.reconciliationLoading = false;
			r.matchDo(
				view => { this.reconciliation = view; this.pruneSelection(); },
				_ => { this.reconciliation = null; this.selectedChangeKeys.clear(); });
		});
	}

	applyReconciliation(): void {
		const caseId = this.activeWorkflowCase?.caseId;
		if (!caseId || !this.reconciliation) return;
		const changes: ReconChange[] = this.reconciliation.stages
			.reduce<ReconChange[]>((acc, s) => acc.concat(s.changes), [])
			.filter(c => this.selectedChangeKeys.has(this.changeKey(c)));
		if (changes.length === 0 || this.reconciliationApplying) return;
		this.reconciliationApplying = true;
		this.svc.applyReconciliation(caseId, changes).then(r => r.matchDo(
			view => {
				this.reconciliationApplying = false;
				this.reconciliation = view;
				this.selectedChangeKeys.clear();
				this.snackBar.open('Case tasks updated', null, {duration:3000});
			},
			(msg: string) => {
				this.reconciliationApplying = false;
				this.snackBar.open('Could not update case tasks: ' + msg, null, {duration:10000});
			}));
	}

	changeKindLabel(kind: string): string {
		switch (kind) {
			case 'Add': return 'Add task';
			case 'Cancel': return 'Remove task';
			case 'ReopenAdd': return 'Reopen & add task';
			default: return kind;
		}
	}

	navigationHotkeys: object;

	//TODO: get counties
	private allCounties = [{"id":1,"name":"Alameda"},{"id":2,"name":"Alpine"},{"id":3,"name":"Amador"},{"id":4,"name":"Butte"},{"id":5,"name":"Calaveras"},{"id":6,"name":"Colusa"},{"id":7,"name":"Contra Costa"},{"id":8,"name":"Del Norte"},{"id":9,"name":"El Dorado"},{"id":10,"name":"Fresno"},{"id":11,"name":"Glenn"},{"id":12,"name":"Humboldt"},{"id":13,"name":"Imperial"},{"id":14,"name":"Inyo"},{"id":15,"name":"Kern"},{"id":16,"name":"Kings"},{"id":17,"name":"Lake"},{"id":18,"name":"Lassen"},{"id":19,"name":"Los Angeles"},{"id":20,"name":"Madera"},{"id":21,"name":"Marin"},{"id":22,"name":"Mariposa"},{"id":23,"name":"Mendocino"},{"id":24,"name":"Merced"},{"id":25,"name":"Modoc"},{"id":26,"name":"Mono"},{"id":27,"name":"Monterey"},{"id":28,"name":"Napa"},{"id":29,"name":"Nevada"},{"id":30,"name":"Orange"},{"id":31,"name":"Placer"},{"id":32,"name":"Plumas"},{"id":33,"name":"Riverside"},{"id":34,"name":"Sacramento"},{"id":35,"name":"San Benito"},{"id":36,"name":"San Bernardino"},{"id":37,"name":"San Diego"},{"id":38,"name":"San Francisco"},{"id":39,"name":"San Joaquin"},{"id":40,"name":"San Luis Obispo"},{"id":41,"name":"San Mateo"},{"id":42,"name":"Santa Barbara"},{"id":43,"name":"Santa Clara"},{"id":44,"name":"Santa Cruz"},{"id":45,"name":"Shasta"},{"id":46,"name":"Sierra"},{"id":47,"name":"Siskiyou"},{"id":48,"name":"Solano"},{"id":49,"name":"Sonoma"},{"id":50,"name":"Stanislaus"},{"id":51,"name":"Sutter"},{"id":52,"name":"Tehama"},{"id":53,"name":"Trinity"},{"id":54,"name":"Tulare"},{"id":55,"name":"Tuolumne"},{"id":56,"name":"Ventura"},{"id":57,"name":"Yolo"},{"id":58,"name":"Yuba"}];

	selectedCounty = null;

	get availableCounties(){
		let countyIdMap = this.case.countyAssignments.reduce((dict,c) => {
			dict[c.id] = c.id;
			return dict;
		},{});
		return this.allCounties.filter(c => !(c.id in countyIdMap));
	}

	removeCountyAssignment(c){
		let i = this.case.countyAssignments.indexOf(c);
		if(i < 0){
			this.snackBar.open('Unable to remove county assignment');
			return;
		}
		if(!confirm('Are you sure?')){
			return;
		}
		this.svc.removeCountyAssignment(this.case.id, c.id).then(r =>
			r.matchDo(
				_ => {
					this.snackBar.open('County assignment removed: ' + c.name);
					this.gaService.eventEmitter("Case County Removed", "Case County", "Removed");
				},
				(msg: string) => this.snackBar.open('Unable to remove county assignment: ' + msg)));
	}
	addCountyAssignment(id){
		let c = this.availableCounties.filter(c => c.id == id)[0];
		if(!c){
			this.snackBar.open('Unable to add county assignment');
			return;
		}
		this.svc.addCountyAssignment(this.case.id, id).then(r =>
			r.matchDo(
				_ => {
					this.snackBar.open('County assignment added: ' + c.name);
					this.gaService.eventEmitter("Case County Added", "Case County", "Added");
					this.selectedCounty = null;
				},
				(msg: string) => this.snackBar.open('Unable to add county assignment: ' + msg)));
	}
	getId(o){
		return o.id;
	}
	actionProposalTypes = [
		{
			value: ActionProposalType.AdvancementTo,
			label: "Advancement"
		},
		{
			value: ActionProposalType.AnnualEvaluation,
			label: "Annual Evaluation"
		},
		{
			value: ActionProposalType.Goals,
			label: "Goals"
		},
		{
			value: ActionProposalType.Deferral,
			label: "Deferral"
		},
		{
			value: ActionProposalType.FiveYearReview,
			label: "Five Year Review"
		}
	];
	Action = ActionType;
	case: Case;
	news: NewsStory[];
	closing = false;
	proposedAction: Option<Action> = Option.create<Action>();
	proposedActionType: ActionType = null;
	availableProposedActions: Option<Action>[] = [];
	eligibleActionTypes: ActionType[] = [];
	eligibleActionByType: Map<ActionType,Action> = new Map();
	activeWorkflowCase: ActiveWorkflowCase | null = null;
	reconciliation: CaseReconciliation | null = null;
	reconciliationLoading = false;
	reconciliationApplying = false;
	private selectedChangeKeys = new Set<string>();
	navigator: CaseModalNavigator;

	preview(html: string){
		HtmlContentModalComponent.open(this.matDialog, "Letters Of Evaluation Request Message", html, '50vw', 'text-sm');
	}

	supervisorReviewParticipationOptions = [];

	supervisorReviewParticipationLabelById = {};

	promoteSupervisor(s: Supervisor, index: number){
	  if(this.validatePromoteReadWriteAccess(s.reviewParticipation, index)){
		if(!confirm('Are you sure?')){
			return;
		}
		this.svc.promoteSupervisor(this.case.id, s.details.id).then(r =>
			r.matchDo(
				_ => {
					this.snackBar.open('Supervisor promoted');
					this.gaService.eventEmitter("Case Supervisor Promoted", "Case Supervisor", "Promoted");
					this.refreshReconciliation();
				},
				(msg: string) => this.snackBar.open('Supervisor not promoted: ' + msg)));
	   }
	}
	demoteSupervisor(s: Supervisor, index: number){
	  if(this.validateDemoteReadWriteAccess(s.reviewParticipation, index)){
		if(!confirm('Are you sure?')){
			return;
		}
		this.svc.demoteSupervisor(this.case.id, s.details.id).then(r =>
			r.matchDo(
				_ => {
					this.snackBar.open('Supervisor demoted');
					this.gaService.eventEmitter("Case Supervisor Demoted", "Case Supervisor", "Demoted");
					this.refreshReconciliation();
				},
				(msg: string) => this.snackBar.open('Supervisor not demoted: ' + msg)));
	  }
	}
	removeSupervisor(s: Supervisor, index: number){
	  if(this.validateRemoveReadWriteAccess(index)){
		if(!confirm('Are you sure?')){
			return;
		}
		this.svc.removeSupervisor(this.case.id, s.details.id).then(r =>
			r.matchDo(
				_ => {
					this.snackBar.open('Supervisor removed');
					this.gaService.eventEmitter("Case Supervisor Removed", "Case Supervisor", "Removed");
					this.refreshReconciliation();
				},
				(msg: string) => this.snackBar.open('Supervisor not removed: ' + msg)));
	  }
	}
	supervisorReviewParticipationChange(index, $event){
		if(!confirm('Are you sure?')){
			$event.preventDefault();
			return;
		}
		var s = this.case.supervisors[index];
		if(!s){
			alert('supervisor not found');
		}
		this.console.log('supervisor review participation change:', s, $event);
		if(this.validateReadWriteAccessOnChange($event.target.value,index)){
			this.svc.addSupervisor(this.case.id, s.details.id, $event.target.value).then(r =>
			r.matchDo(
				_ => {
					this.snackBar.open('Supervisor updated');
					this.gaService.eventEmitter("Case Supervisor Updated", "Case Supervisor", "Updated");
					this.refreshReconciliation();
				},
				(msg: string) => this.snackBar.open('Supervisor not updated: ' + msg)));
		}
	}
	addSupervisor(s: User, reviewParticipationId: string){
		if(this.validateReadWriteAccess(reviewParticipationId)){
		 this.svc.addSupervisor(this.case.id, s.id, reviewParticipationId).then(r =>
			r.matchDo(
				_ => {
					this.snackBar.open('Supervisor added');
					this.gaService.eventEmitter("Case Supervisor Added", "Case Supervisor", "Added");
					this.refreshReconciliation();
				},
				(msg: string) => this.snackBar.open('Supervisor not added: ' + msg)));
		}
	}
	public IsIndefiniteStatusOnly(){
		var proposedAction = this.case.proposedAction.getValueOrDefault(null);
		return Position.titleRankStepEqual(this.case.currentPosition, proposedAction.endingPosition)
			&& this.indefiniteStatusProposed();
	}

	public indefiniteStatusProposed() {
		var proposedAction = this.case.proposedAction.getValueOrDefault(null);
		return this.case.currentPosition.term != 'Indefinite Status' && proposedAction.endingPosition.term == 'Indefinite Status';
	}

    public n_ary(n: number): string {
        switch(n){
            case 1: return 'Primary';
            case 2: return 'Secondary';
            case 3: return 'Additional';
            default: return 'Additional';
        }
    }

	public static open(matDialog: MatDialog, c: Case, navigator?: CaseModalNavigator): MatDialogRef<CaseModalComponent>{
		var instance = matDialog.open(CaseModalComponent, {
			width: '1200px',
			disableClose: false,
			data: {
				navigator
		  	},
			hasBackdrop: true
		});

		instance.componentInstance.viewCase(c);

		return instance;
	}

	public viewCase(c: Case): void {
		this.case = c;
		this.console.log("Case: ", this.case);
		this.proposedAction = this.case.proposedAction;
		this.proposedActionType = this.case.proposedAction.map(pa => pa.type).getValueOrDefault(null);
		this.console.log("Proposed Action: ", this.proposedAction);
		this.availableProposedActions = this.case.eligibleActions.map(Option.create);

		this.tabs = this.isNonAcademic(c) ? this.nonacademic_tabs: this.academic_tabs;
		this.activeTab = this.tabs[0];
		this.updateHistoryLayout();

		this.eligibleActionTypes = 
			this.case.eligibleActions.map(a => a.type);
		this.eligibleActionByType = 
			new Map<ActionType,Action>(
				this.case.eligibleActions.map(a => [a.type, a] as [ActionType, Action]));
		this.svc
			.newsStoriesByCaseId(this.case.id)
			.subscribe(news => this.news = news);
		let initialEmission = true;
		this.svc
			.cases
			.pipe(map(cases => cases.find(c => c.id == this.case.id)))
			.pipe(filter(c => c != undefined))
			.subscribe(c => {
				if (initialEmission) {
					initialEmission = false;
					return; // we fetch below; skip the duplicate
				}
				// The list payload is thin (no review steps/committees), so a list
				// refresh can't replace the modal's case directly — re-fetch the
				// full detail instead. The thin emission still carries the fresh
				// state, which drives the workflow-case refresh below.
				this.fetchFullCase(c.id);
				// Refresh the workflow case on any state where a workflow.Case row
				// could exist. The historical WFACaseBridge recall race that caused
				// this to clear for ReadyToSendToCandidate is gone now —
				// CaseLifecycleService.RecallCase cancels the old row and creates the
				// new Proposed row in one transaction, so a refresh here cannot land
				// on an inconsistent mid-state. Keeping AwaitingProposal/NotApplicable
				// in the null branch because those never have a workflow row the
				// active-packets endpoint will return.
				if (c.state === CaseState.ReadyToSendToCandidate
					|| c.state === CaseState.ReadyToApproveDeferral
					|| c.state === CaseState.WithCandidate
					|| c.state === CaseState.InReview
					|| c.state === CaseState.InAppeal
					|| c.state === CaseState.Complete
					|| c.state === CaseState.Stalled) {
					this.refreshActiveWorkflowCase();
				} else {
					this.activeWorkflowCase = null;
				}
			});

		this.activeWorkflowCase = null;
		this.refreshActiveWorkflowCase();
		this.fetchFullCase(c.id);
	}

	// Callers open the modal with a case from the thin list payload; pull the fully
	// projected case (review steps, committees, supervisor instructions) for the detail
	// view. The id guard drops a stale response if the user has navigated to another case.
	private fetchFullCase(caseId: string): void {
		this.svc.getCaseById(caseId).then(full => full.matchDo(
			() => {},
			(fullCase: Case) => {
				if (this.case && this.case.id === caseId) {
					this.case = fullCase;
				}
			}));
	}

	constructor(
		public dialogRef: MatDialogRef<CaseModalComponent>,
		@Inject(MAT_DIALOG_DATA) private data: any,
		private console: ConsoleService,
		private svc: AHRService,
		private snackBar: MatSnackBar,
		private gaService: GoogleAnalyticsService,
		private readonly matDialog: MatDialog,
		private workflowSvc: WorkflowService
	){
		this.usersControl = new FormControl(new SortedSet<User>());

		this.supervisorReviewParticipationOptions = [
			{
				id:"WritesReview",
				label: "Writes Review",
				description: "This supervisor writes a review"
			},
			{
				id:"ReadOnly",
				label: "Read-only access",
				description: "This supervisor will have read-only access to candidate materials but will not be asked to write a review"
			}
		];

		this.supervisorReviewInvolvementControl = new FormControl(this.supervisorReviewParticipationOptions[0].id);
		this.supervisorReviewParticipationLabelById = Object.fromEntries(this.supervisorReviewParticipationOptions.map(o => [o.id, o.label]));

		if(!!data.navigator){
			this.navigator = <CaseModalNavigator>data.navigator;
			this.console.log('Case navigator provided:', this.navigator);
		} else {
			this.navigator = CaseModalNavigator.noop();
			this.console.log('No case navigator provided');
		}
		this.console.log(this.svc);
		this.navigationHotkeys = {
			'ArrowLeft': this.navigator.tryGoPrev.bind(this.navigator,this),
			'ArrowRight': this.navigator.tryGoNext.bind(this.navigator,this)
		};
	}

	private tryOpenAdvancementSelectorModalComponent(showAdminAssignment: boolean = false): Option<MatDialogRef<AdvancementSelectorModalComponent>>
	{
		return this.case.progressionTable.match(
			() => {
				alert('Unable to load progression table for this person');
				return Option.create<MatDialogRef<AdvancementSelectorModalComponent>>();
			},
			(table: ProgressionTable) => {
				let selectedPosition = this.case.proposedAction.map((a:Action) =>
					new TitleRankStepAndTermId(
						a.endingPosition.id.titleId,
						a.endingPosition.id.rankId,
						a.endingPosition.id.step,
						a.endingPosition.id.term.getValueOrDefault(null))).getValueOrDefault(null);
				let currentPosition = new TitleRankStepAndTermId(
					this.case.currentPosition.id.titleId,
					this.case.currentPosition.id.rankId,
					this.case.currentPosition.id.step,
					this.case.currentPosition.id.term.getValueOrDefault(null)
				);
				// Re-proposing from a Complete case makes the backend create a new
				// workflow.Case row whose AdminOverridesJSON starts null. Seed the
				// modal with false in that case so the checkbox matches what the
				// new row will actually hold — otherwise the old row's value would
				// be visually preselected but silently dropped if the admin leaves
				// it alone (unchanged checkbox = no POST to apply it to the new row).
				const reProposeCreatesNewRow = this.case.state === CaseState.Complete;
				const initialAdminAssignment = reProposeCreatesNewRow
					? false
					: !!this.activeWorkflowCase?.hasAdminAssignment;
				return Option.create(
					AdvancementSelectorModalComponent.open(
						this.matDialog,
						table,
						currentPosition,
						selectedPosition,
						this.case.timeServed,
						showAdminAssignment,
						initialAdminAssignment))
			});
	}

	propose(t: ActionProposalType){
		if(t == ActionProposalType.AdvancementTo){
			this.tryOpenAdvancementSelectorModalComponent(/*showAdminAssignment*/ true).doWithValue(
				(dialogRef: MatDialogRef<AdvancementSelectorModalComponent>) =>
				{
					dialogRef.afterClosed().subscribe((result: AdvancementSelectorResult | undefined) => {
						if(!result || !result.position){
							return;
						}
						const position = result.position;
						let hasTermChanged = Case.isProposedTermDifferentFromCurrentTerm(this.case.currentPosition, position.termId);
						const proposal = new ActionProposal(
							ActionProposalType.AdvancementTo,
							new Advancement(
								position.toTitleRanksAndStepId(),
								Option.create(position.termId).map(t => nextTerm(t, hasTermChanged))
							));
						// Apply admin-assignment after the proposal commits — the workflow.Case
						// row for a re-propose-after-Complete is created by proposeAction, so
						// the caseId has to be resolved fresh.
						this.send(proposal, () => {
							if(result.adminAssignmentChanged){
								this.applyAdminAssignmentChange(result.adminAssignment);
							}
						});
					})
				});
		} else if (t == ActionProposalType.Deferral) {
			let reason = prompt('Reason for Deferral (be brief):');
			if(!reason){
				alert('You must provide a reason for the deferral');
				return;
			}
			this.svc.proposeDeferral(this.case.id, reason).then(result => 
				result.matchDo(
					_   => this.snackBar.open('Deferral Proposed', null, {duration:3000}),
					msg => this.snackBar.open('Unable to defer review: ' + msg, null, {duration:10000})));
		} else {
			this.send(new ActionProposal(t, null));
		}
	}

	private send(p: ActionProposal, onSuccess?: () => void){
		this.console.log('proposal: ', p);
		this.svc.proposeAction(this.case.id, p).then(result =>
			result.matchDo(
				_ => {
					this.snackBar.open('Proposed Action updated', null, {duration:3000});
					this.gaService.eventEmitter("Case Proposed Action Updated", "Case Proposed Action", "Updated");
					if(onSuccess) onSuccess();
					//TODO: merge new proposal back into the case read model
				},
				msg => {
					this.snackBar.open('Proposed Action not updated: ' + msg, null, {duration:10000});
				}));
	}

	/** Re-resolves the current workflow caseId (since re-propose-after-Complete creates a new row) and POSTs the admin-assignment flag. */
	private applyAdminAssignmentChange(value: boolean){
		// Error messages here follow the "Proposal saved — admin assignment ..." pattern
		// because this runs AFTER a successful proposeAction. Without that framing the
		// admin sees a green "Proposed Action updated" toast followed by a red "Could
		// not update admin assignment" toast and can reasonably wonder whether the
		// proposal itself rolled back (it didn't — they'd need to re-click Propose to
		// retry the admin-assignment half, which safely updates the same row).
		this.workflowSvc.getPacketByAcademic(this.case.id).then(
			packet => {
				this.workflowSvc.setAdminAssignment(packet.caseId, value).then(
					res => {
						if (this.activeWorkflowCase) this.activeWorkflowCase.hasAdminAssignment = res.hasAdminAssignment;
						this.snackBar.open(
							res.hasAdminAssignment ? 'Admin assignment granted' : 'Admin assignment removed',
							null, { duration: 3000 });
					},
					err => this.snackBar.open('Proposal saved — but admin assignment could not be applied: ' + (err.error?.error || err.message), null, { duration: 10000 })
				);
			},
			err => this.snackBar.open('Proposal saved — but admin assignment could not be applied (case lookup failed): ' + (err.error?.error || err.message), null, { duration: 10000 })
		);
	}
 
	private sendDenial(secondaryAction: any){
		this.svc.denyProposedAdvancement(this.case.id, secondaryAction,this.proposedActionType)
		.then(result => 
			result.matchDo(
				_   => {
					this.snackBar.open('Proposed advancement denied', null, {duration:10000});
					this.gaService.eventEmitter("Case Proposed Advancement Denied", "Case Proposed Advancement", "Denied");
				},
				msg => this.snackBar.open('Unable to deny advancement:' + msg, null, {duration:10000})));
	}
	private sendFiveyearDenial(secondaryAction: any){
		this.svc.denyProposedAdvancement(this.case.id, secondaryAction,this.proposedActionType)
		.then(result => 
			result.matchDo(
				_   => {
					this.snackBar.open('Proposed five year review denied', null, {duration:10000});
					this.gaService.eventEmitter("Case Proposed five year review Denied", "Case Proposed five year review", "Denied");
				},
				msg => this.snackBar.open('Unable to deny five year review:' + msg, null, {duration:10000})));
	}
	public approve(){
		this.svc.approveProposedAdvancement(this.case.id, this.case.yearId,this.proposedActionType)
		.then(result => 
			result.matchDo(
				_   => {
					this.snackBar.open('Proposed advancement approved', null, {duration:10000});
					this.gaService.eventEmitter("Case Proposed Advancement Approved", "Case Proposed Advancement", "Approved");
				},
				msg => this.snackBar.open('Unable to approve advancement:' + msg, null, {duration:10000})));
	}
	
	public denialOptions = [
		{
			label: 'Dismiss/Demote',
			value: 'DismissOrDemote'
		},
		{
			label: 'Extend Term',
			value: 'ExtendedTerm'
		},
		{
			label: 'Advance with term extension',
			value: 'AdvancementWithTermExtension'
		},
		{
			label: 'Advance Term',
			value: 'TermAdvancement'
		},
		{
			label: 'Just \'No\'',
			value: 'None'
		},
		{
			label: 'Alternative Advancement',
			value: 'AlternativeAdvancement'
		}
	];

	public deny(t){
		function some(value){ return {"case":"Some","fields":[value]}; }
		function none(){ return {"case":"None"}; }

		switch(t){
			case "DismissOrDemote":
				if(confirm('Begin dismissal/demotion? Are you sure?')){
					this.sendDenial(some({'case': 'DismissOrDemote'}));
				}
				break;
			case "ExtendedTerm":
				let years = prompt('How many years would you like to extend the current term?','1');
				if(years && parseInt(years)){
					this.sendDenial(some({'case': 'ExtendedTerm', "fields":[parseInt(years)]}));
				} else {
					alert('Invalid years selection.  Please try again.');
				}
				break;
			case "TermAdvancement":
				if(confirm('Begin term advancement? Are you sure?')){
					this.sendDenial(some({'case': 'TermAdvancement'}));
				}
				break;
			case "TermAdvancement":
				if(confirm('Begin term advancement? Are you sure?')){
					this.sendDenial(some({'case': 'TermAdvancement'}));
				}
				break;
			case "AdvancementWithTermExtension":
				if(confirm('Advance to the proposed position and extend the current term?')){
					let newPositionId = this.case.proposedAction.map((a:Action) => 
						new TitleRankStepAndTermId(
							a.endingPosition.id.titleId,
							a.endingPosition.id.rankId,
							a.endingPosition.id.step,
							a.endingPosition.id.term.getValueOrDefault(null))).getValueOrDefault(null);
					this.sendDenial(some({'case': 'AdvancementWithTermExtension','fields':[newPositionId]}));
				}
				break;
			case "AlternativeAdvancement":
				this.tryOpenAdvancementSelectorModalComponent(/*showAdminAssignment*/ false).doWithValue(
					(dialogRef: MatDialogRef<AdvancementSelectorModalComponent>) =>
						dialogRef.afterClosed().subscribe((result: AdvancementSelectorResult | undefined) => {
							const position = result?.position;
							if(!position){
								//alert('Invalid advancement selection.  Please try again.')
								return;
							}
							let hasTermChanged = Case.isProposedTermDifferentFromCurrentTerm(this.case.currentPosition, position.termId);
							let adv = 
								{
									positionId: position.toTitleRanksAndStepId(),
									term: Option.create(position.termId).map(t => nextTerm(t, hasTermChanged))
										.map(t => ({"case":t}))
										.toFSharp()
								};
							this.sendDenial(some({'case': 'AlternativeAdvancement', "fields":[adv]}));
						}));
				break;
			case "None":
				if(confirm('Just say \'No\'? Are you sure?')){
					this.sendDenial(none());
				}
				break;
			default:
				alert('Unrecognized denial type: ' + t);
				break;
		}
	}
    public denyFiveyearReview(){
		function none(){ return {"case":"None"}; }
		if(confirm('Just say \'No\'? Are you sure?')){
			this.sendFiveyearDenial(none());
		}
	}
	public canDenyFiveyearReview(){
		return (this.case.state == CaseState.WithCandidate || this.case.state == CaseState.InReview || this.case.state == CaseState.InAppeal)
			&& this.proposedActionType === ActionType.FiveYearReview;
	}
	public get approvalLabel(){
		return this.case.state == CaseState.InAppeal
		? "Approve appeal"
		: "Approve proposed action";
	}

	public get denialLabel(){
		return this.case.state == CaseState.InAppeal
		? "Deny appeal"
		: "Deny proposed action";
	}

	public canApprove(){
		return (this.case.state == CaseState.WithCandidate || this.case.state == CaseState.InReview || this.case.state == CaseState.InAppeal)
			&& (this.proposedAnAdvancement || this.proposedActionType === ActionType.FiveYearReview);
	}
	public canDenyProposedAction(){
		return (this.case.state == CaseState.WithCandidate || this.case.state == CaseState.InReview || this.case.state == CaseState.InAppeal)
			&& this.proposedAnAdvancement;
	}
	public canDenyResult(){
		return this.case.hasPreviousYear 
			&& this.case.state == CaseState.AwaitingProposal;
	}
	private get proposedAnAdvancement(){
		return this.case.proposedAction
			.map(a => a.type == ActionType.Merit || a.type == ActionType.Promotion)
			.getValueOrDefault(false);
	}

	canSendToCandidate(){
		return this.case.state == CaseState.ReadyToSendToCandidate;
	}

	canAppealResult(){
		// Only action types with approval/denial commands can be appealed
		// (Merit, Promotion, FiveYearReview). Goals and AnnualEvaluation have
		// no resolution path and would get stuck in InAppeal.
		const appealable = this.proposedAnAdvancement || this.proposedActionType === ActionType.FiveYearReview;
		if (!appealable) return false;
		return this.case.state == CaseState.Complete
			|| (this.case.hasPreviousYear && this.case.state == CaseState.AwaitingProposal)
			|| (!this.case.hasPreviousYear && this.proposedAnAdvancement);
	}

	canRecall(){
		return this.case.state == CaseState.WithCandidate || this.case.state == CaseState.InReview || this.case.state == CaseState.Stalled;
	}

	get hasActiveWorkflow(): boolean {
		const s = this.activeWorkflowCase?.status;
		return s === 'Active' || s === 'Stalled' || s === 'InAppeal' || s === 'Complete';
	}

	get workflowStageLabel(): string {
		if (!this.hasActiveWorkflow) return '';
		// CurrentStage is frozen/meaningless for the parallel-stage advancement types:
		// the case state beside it is derived from the gates and is authoritative, so
		// showing a stale "(Candidate Submission)" would contradict it. Suppress it.
		// Annual Evaluation and Goals are excluded — their pointer does advance.
		if (FROZEN_STAGE_WORKFLOW_TYPES.includes(this.activeWorkflowCase.workflowType)) return '';
		return STAGE_DISPLAY_NAMES[this.activeWorkflowCase.currentStage] || this.activeWorkflowCase.currentStage;
	}

	canApproveDeferral(){
		return this.case.state == CaseState.ReadyToApproveDeferral;
	}

	canPropose(){
		return this.case.state != CaseState.WithCandidate && this.case.state != CaseState.InReview;
	}

	appealResult(){
		this.svc.appealResult(this.case.id).then(result => 
			result.matchDo(
				_   => {
					this.snackBar.open('Result appealed', null, {duration:3000});
					this.gaService.eventEmitter("Case Result Appealed", "Case Result", "Appealed");
				},
				msg => this.snackBar.open('Unable to appeal case: ' + msg, null, {duration:10000})));
	}

	sendToCandidate(){
		this.svc.sendToCandidate(this.case.id).then(result => 
			result.matchDo(
				_   => {
					this.snackBar.open('Case sent to candidate', null, {duration:3000});
					this.gaService.eventEmitter("Case Sent", "Case", "Sent");
				},
				msg => this.snackBar.open('Unable to send case to candidate: ' + msg, null, {duration:10000})));
	}

	refreshActiveWorkflowCase() {
		this.workflowSvc.getActivePacketsByAcademic(this.case.id).then(
			cases => { this.activeWorkflowCase = cases[0] || null; this.refreshReconciliation(); },
			() => {} // No workflow case — that's fine
		);
	}

	canToggleAdminAssignment(): boolean {
		if (!this.activeWorkflowCase) return false;
		if (!this.activeWorkflowCase.supportsAdminAssignment) return false;
		// Kept in sync with CaseLifecycleService.SetAdminAssignment which rejects
		// Complete/Cancelled/Expired. The active-packets endpoint already excludes
		// Expired, so that branch is defensive — but if the endpoint filter widens
		// later, this guard keeps us aligned with server authorization.
		const terminal = this.activeWorkflowCase.status === 'Complete'
			|| this.activeWorkflowCase.status === 'Cancelled'
			|| this.activeWorkflowCase.status === 'Expired';
		return !terminal;
	}

	toggleAdminAssignment(value: boolean) {
		if (!this.activeWorkflowCase) return;
		const caseId = this.activeWorkflowCase.caseId;
		this.workflowSvc.setAdminAssignment(caseId, value).then(
			res => {
				if (this.activeWorkflowCase) this.activeWorkflowCase.hasAdminAssignment = res.hasAdminAssignment;
				// Defensive: the event-store event triggers a SignalR case update
				// whose observer can race with this .then — refetch to ensure
				// activeWorkflowCase stays populated regardless of ordering.
				this.refreshActiveWorkflowCase();
				this.snackBar.open(
					res.hasAdminAssignment ? 'Admin assignment granted' : 'Admin assignment removed',
					null, { duration: 3000 });
			},
			err => this.snackBar.open('Could not update admin assignment: ' + (err.error?.error || err.message), null, { duration: 10000 })
		);
	}

	canRetryStalledCase(){
		return this.case.state === CaseState.Stalled && this.activeWorkflowCase?.status === 'Stalled';
	}

	retryStalledCase(){
		if (!this.activeWorkflowCase) return;
		const caseId = this.activeWorkflowCase.caseId;
		this.workflowSvc.retryStalledCase(caseId).then(
			() => {
				// Re-fetch to see if it's still stalled or advanced
				this.workflowSvc.getActivePacketsByAcademic(this.case.id).then(cases => {
					this.activeWorkflowCase = cases[0] || null;
					if (this.activeWorkflowCase?.status === 'Stalled') {
						this.snackBar.open('Workflow is still stalled: ' + (this.activeWorkflowCase.stallReason || 'unknown reason'), null, {duration:10000});
					} else {
						this.snackBar.open('Workflow advanced successfully', null, {duration:3000});
					}
				});
			},
			(err) => this.snackBar.open('Retry failed: ' + (err.error?.error || err.message), null, {duration:10000})
		);
	}

	recallCase(){
		let reason = prompt('Why do you wish to recall the case from the candidate? (be brief)');
		if(reason === null) return;
		if(!reason.trim()){
			alert('You must provide a reason for the recall');
			return;
		}
		this.svc.recallFromCandidate(this.case.id, reason).then(result =>
			result.matchDo(
				_ => {
					// Backend cancels the workflow case automatically on recall.
					// Clear optimistically — the DB cancellation may not have
					// committed yet, so re-fetching would race and still find the
					// active row.  SignalR will refresh again once the dust settles.
					this.activeWorkflowCase = null;
					this.snackBar.open('Case recalled', null, {duration:3000});
					this.gaService.eventEmitter("Case Recalled", "Case", "Recalled");
				},
				msg => this.snackBar.open('Unable to recall case: ' + msg, null, {duration:10000})));
	}

	approveDeferral(){
		this.svc.approveDeferral(this.case.id).then(result => 
			result.matchDo(
				_   => {
					this.snackBar.open('Deferral Approved', null, {duration:3000});
					this.gaService.eventEmitter("Case Deferral Approved", "Case Deferral", "Approved");
				},
				msg => this.snackBar.open('Unable to approve deferral: ' + msg, null, {duration:10000})));
	}

	close(){
		this.closing = true;
		this.dialogRef.close('cancelled');
	}
	validateReadWriteAccess(reviewParticipationId:string){
		let lastSupervisor= this.case?.supervisors && this.case?.supervisors[this.case?.supervisors.length-1];
		if((reviewParticipationId === "ReadOnly" &&  this.case?.supervisors.length === 0)
		   || ( reviewParticipationId ===  "WritesReview" && lastSupervisor?.reviewParticipation === "ReadOnly"))
		{
		 this.openValidationMessage();
		 return false;
		}
		else {
		 return true;
		}
	 }
	 validateReadWriteAccessOnChange(reviewParticipationId:string, index: number){
		 let priorSupervisor = this.case?.supervisors && this.case?.supervisors[index-1];
		 let nextSupervisor = this.case?.supervisors && this.case?.supervisors[index+1];
		 if((reviewParticipationId === "WritesReview" && priorSupervisor?.reviewParticipation === "ReadOnly")
		   || (reviewParticipationId === "ReadOnly" && nextSupervisor?.reviewParticipation === "WritesReview")
		   || (index === 0 && reviewParticipationId === "ReadOnly")){
			 this.openValidationMessage();
			 return false;
		 }
		 else{
			return true;
		 }
	 }
	 validatePromoteReadWriteAccess(reviewParticipationId:string, index: number){
		let priorSupervisor= this.case?.supervisors && this.case?.supervisors[index-1];
		if(reviewParticipationId !==  priorSupervisor?.reviewParticipation)
		{
		 this.openValidationMessage();
		 return false;
		}
		else {
		 return true;
		}
	 }
	 validateDemoteReadWriteAccess(reviewParticipationId:string, index: number){
		let nextSupervisor = this.case?.supervisors && this.case?.supervisors[index+1];
		if(reviewParticipationId !==  nextSupervisor?.reviewParticipation)
		{
		 this.openValidationMessage();
		 return false;
		}
		else {
		 return true;
		}
	 }
	 validateRemoveReadWriteAccess(index: number){
		let nextSupervisor = this.case?.supervisors && this.case?.supervisors[index+1];
		if(index === 0 && nextSupervisor?.reviewParticipation === "ReadOnly")
		{
		 this.openValidationMessage();
		 return false;
		}
		else {
		 return true;
		}
	 }
	 openValidationMessage(){
		 let html =`<ul><li>Write, readonly, readonly, readonly, readonly…</li>`+
				 `<li>Write, write, readonly, readonly, readonly…</li>`+
				 `<li>Write, write, write, readonly, readonly…</li></ul>`
		 HtmlContentModalComponent.open(this.matDialog, "Acceptable orders:", html, '30vw', 'text-sm');
	 }

	 public isNonAcademic(c: Case) : boolean {
		const nonAcademicTitle = this.svc.getAllTitlesRanksAndSteps().find(t => t.name === 'Staff Director');
		return c.currentPosition.id.titleId === nonAcademicTitle?.id;
	}

	public userSeparationDateChg(e: MatDatepickerInputEvent<Date>){
		this.case.userSeparationDate = e.value;
	}

	public saveUserSeparationDate(){
		this.svc.updateUserSeparationDate(this.case)
		.then(result => 
			result.matchDo(
				_   => {
					this.snackBar.open('Separation Date updated', null, {duration:3000});
					this.gaService.eventEmitter("Separation Date updated", "Academic Update", "Updated");
				},
				msg => this.snackBar.open('Unable to update separation date: ' + msg, null, {duration:10000})));
	}

	public clearUserSeparationDate(){
		this.case.userSeparationDate = null;
		this.saveUserSeparationDate();
	}

	public showUserSeparationDate(){
		return this.case.userSeparationDate && this.case.userSeparationDate < new Date();
	}

	public formatSeparationDate(){
		return formatShortDate(this.case.userSeparationDate);
	}
}